import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, RefreshCw, Star } from 'lucide-react';
import api from '../api';

const S = `
.cd-wrap{color:#111827}
.cd-back{border:1px solid #d1d5db;background:#fff;color:#111827;border-radius:10px;padding:8px 12px;font-size:13px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;margin-bottom:10px}
.cd-refresh{border:1px solid #d1d5db;background:#fff;color:#111827;border-radius:10px;padding:8px 12px;font-size:13px;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.cd-head{background:#fff;border:1px solid #e5e7eb;border-radius:14px;box-shadow:0 2px 10px rgba(0,0,0,.05);padding:16px;margin-bottom:12px}
.cd-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
.cd-address-card{padding:12px;border:1px solid #e5e7eb;border-radius:12px;background:#f9fafb}
.cd-k{font-size:11px;text-transform:uppercase;color:#1a1a1a;font-weight:700}
.cd-v{font-size:14px;color:#111827;font-weight:600}
.cd-badge{display:inline-flex;align-items:center;gap:4px;border-radius:999px;padding:4px 10px;font-size:11px;font-weight:700}
.cd-new{background:#dbeafe;color:#1d4ed8}
.cd-regular{background:#dcfce7;color:#166534}
.cd-vip{background:#fef3c7;color:#92400e}
.cd-tabs{display:flex;gap:8px;margin-bottom:10px;flex-wrap:wrap}
.cd-tab{border:1px solid #d1d5db;background:#fff;color:#111827;border-radius:10px;padding:8px 12px;font-size:13px;cursor:pointer}
.cd-tab.active{background:#111827;color:#fff;border-color:#111827}
.cd-card{background:#fff;border:1px solid #e5e7eb;border-radius:14px;box-shadow:0 2px 10px rgba(0,0,0,.05);overflow:hidden}
.cd-table-wrap{overflow:auto}
.cd-table{width:100%;border-collapse:collapse;font-size:13px}
.cd-table th{background:#f9fafb;color:#1a1a1a;text-align:left;padding:12px;border-bottom:1px solid #e5e7eb;white-space:nowrap}
.cd-table td{padding:12px;border-bottom:1px solid #f3f4f6;white-space:nowrap;color:#111827}
.cd-empty{padding:24px;text-align:center;color:#1a1a1a}
.cd-sk{height:44px;border-radius:8px;background:linear-gradient(90deg,#e5e7eb 25%,#f3f4f6 50%,#e5e7eb 75%);background-size:200% 100%;animation:shimmer 1.5s infinite;margin:8px}
@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
@media(max-width:768px){.cd-grid{grid-template-columns:1fr}}
`;

function ratingClass(r) {
  if (r === 'VIP') return 'cd-vip';
  if (r === 'REGULAR') return 'cd-regular';
  return 'cd-new';
}

export default function CustomerDetail({ customerId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('orders');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sales/customers/${customerId}`);
      setData(res?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const handleFocus = () => load();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [load]);

  const customer = data?.customer;
  const orders = useMemo(() => data?.orders || [], [data]);
  const quotations = useMemo(() => data?.quotations || [], [data]);

  return (
    <>
      <style>{S}</style>
      <div className="cd-wrap">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <button className="cd-back" onClick={onBack}><ArrowLeft size={14} />Back</button>
          <button className="cd-refresh" onClick={load}><RefreshCw size={14} />Refresh</button>
        </div>

        <div className="cd-head">
          {loading ? (
            <div>{Array.from({ length: 4 }).map((_, i) => <div key={i} className="cd-sk" />)}</div>
          ) : !customer ? (
            <div className="cd-empty">Customer not found</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 900 }}>{customer.name}</div>
                  <div style={{ fontSize: 12, color: '#1a1a1a' }}>{customer.email || '-'} | {customer.phone || '-'}</div>
                </div>
                <span className={`cd-badge ${ratingClass(customer.rating)}`}>{customer.rating === 'VIP' ? <Star size={12} /> : null}{customer.rating}</span>
              </div>
              <div className="cd-grid">
                <div><div className="cd-k">City</div><div className="cd-v">{customer.city || '-'}</div></div>
                <div><div className="cd-k">Outstanding</div><div className="cd-v">Rs {Number(customer.outstanding_balance || 0).toLocaleString('en-IN')}</div></div>
                <div><div className="cd-k">Total Orders</div><div className="cd-v">{customer.total_orders || 0}</div></div>
                <div><div className="cd-k">Total Value</div><div className="cd-v">Rs {Number(customer.total_order_value || 0).toLocaleString('en-IN')}</div></div>
                <div><div className="cd-k">Last Order Date</div><div className="cd-v">{customer.last_order_date ? new Date(customer.last_order_date).toLocaleDateString() : '-'}</div></div>
                <div className="cd-address-card">
                  <div className="cd-k">Shop Address</div>
                  <div className="cd-v">{customer.shop_full_address || customer.full_address || '-'}</div>
                </div>
                <div className="cd-address-card">
                  <div className="cd-k">Home / Permanent Address</div>
                  <div className="cd-v">{customer.home_full_address || '-'}</div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="cd-tabs">
          <button className={`cd-tab ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')}>Orders</button>
          <button className={`cd-tab ${tab === 'quotations' ? 'active' : ''}`} onClick={() => setTab('quotations')}>Quotations</button>
        </div>

        <div className="cd-card">
          <div className="cd-table-wrap">
            {loading ? (
              <div>{Array.from({ length: 8 }).map((_, i) => <div key={i} className="cd-sk" />)}</div>
            ) : tab === 'orders' ? (
              orders.length ? (
                <table className="cd-table">
                  <thead><tr><th>Order No</th><th>Design</th><th>Qty</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>
                    {orders.map((r, i) => (
                      <tr key={`${r.order_number}-${i}`}>
                        <td>{r.order_number || '-'}</td>
                        <td>{r.design || '-'}</td>
                        <td>{r.quantity || 0}</td>
                        <td>Rs {Number(r.amount || 0).toLocaleString('en-IN')}</td>
                        <td>{r.status || '-'}</td>
                        <td>{r.date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="cd-empty">No orders found</div>
            ) : (
              quotations.length ? (
                <table className="cd-table">
                  <thead><tr><th>Quotation No</th><th>Design</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>
                    {quotations.map((r, i) => (
                      <tr key={`${r.number}-${i}`}>
                        <td>{r.number || '-'}</td>
                        <td>{r.design || '-'}</td>
                        <td>Rs {Number(r.amount || 0).toLocaleString('en-IN')}</td>
                        <td>{r.status || '-'}</td>
                        <td>{r.date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="cd-empty">No quotations found</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
